/* eslint-disable no-fallthrough */
/* eslint-disable default-case */
import React, { useState, useEffect } from 'react';
import {
   Table, Space, Tooltip, Button, Input, Row, Col,
   Modal, Typography, Image, Segmented, Select, message
} from 'antd';
import * as AntDIcons from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import axios from 'axios';
import Utils from '../../../misc/Utils';
import {
   EMSSummaryReport,
   MTSummaryReport,
   SHSSummaryReport
} from '../diagnostic-reports';
import { getReportFamily } from '../diagnostic-reports/reportConfigResolver';

import html2pdf from 'html2pdf.js';
import PropTypes from 'prop-types';

const { Text } = Typography;
const { Option } = Select;

/**
 * Displays student summary reports for diagnostics.
 * @component
 * @param {Object} props
 * @param {string|number} props.level - The grade/level identifier.
 * @param {string} props.type - The diagnostic type (e.g., 'pre', 'post').
 * @param {string} [props.template] - The template/grade name.
 * * @param {string|number} [props.school_year] - The school year.
 */

BStudentSummaryReports.propTypes = {
   level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
   type: PropTypes.string.isRequired,
   template: PropTypes.string,
   school_year: PropTypes.string.isRequired,
};

export function BStudentSummaryReports(props) {
   const [searchText, setSearchText] = React.useState("");
   const [searchedColumn, setSearchedColumn] = React.useState("");

   const [diagnosticList, setDiagnosticList] = useState([]);
   const [showSummaryReport, setShowSummaryReport] = useState(false);
   const [listLoading, setListLoading] = useState(false);
   const [downloadStatus, setDownloadStatus] = useState(false);
   const [sectionList, setSectionList] = useState(null);
   const [sectionOption, setSectionOption] = useState(0);
   const [selectedSections, setSelectedSections] = useState("-1");
   const [selectedSectionsDesc, setSelectedSectionsDesc] = useState("-1");
   const [assessmentID, setAssessmentID] = useState(null);
   const [subject, setSubject] = useState(null);
   const [subjectDesc, setSubjectDesc] = useState(null);
   const [template, setTemplate] = useState(null);

   const level = props.level;
   const type = props.type;

   useEffect(() => {
      Utils.verifyJwt();

      getDiagnosticTitles();
   }, []);

   const handleSearch = (selectedKeys, confirm, dataIndex) => {
      confirm();
      setSearchText(selectedKeys[0]);
      setSearchedColumn(dataIndex);
   };

   const handleReset = clearFilters => {
      clearFilters();
      setSearchText("");
   };

   const getColumnSearchProps = dataIndex => ({
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
         <div style={{ padding: 8 }}>
            <Input
               ref={node => {
               }}
               placeholder={`Search ${dataIndex}`}
               value={selectedKeys[0]}
               onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
               onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
               style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Space>
               <Button
                  type="primary"
                  onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                  icon={<AntDIcons.SearchOutlined />}
                  size="small"
                  style={{ width: 90 }}>
                  Search
               </Button>
               <Button
                  size="small"
                  style={{ width: 90 }}
                  onClick={() => {
                     handleReset(clearFilters);
                     confirm({ closeDropdown: false });
                     setSearchText(selectedKeys[0]);
                     setSearchedColumn(dataIndex);
                  }}
               >
                  Reset
               </Button>
            </Space>
         </div>
      ),
      filterIcon: filtered => <AntDIcons.SearchOutlined style={{ color: filtered ? '#033D89' : undefined }} />,
      onFilter: (value, record) =>
         record[dataIndex]
            ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
            : '',
      render: text =>
         searchedColumn === dataIndex ? (
            <Highlighter
               highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
               searchWords={[searchText]}
               autoEscape
               textToHighlight={text ? text.toString() : ''}
            />
         ) : (
            text
         ),
   });

   const columns = [
      {
         title: 'Diagnostic Title',
         key: 'title',
         ...getColumnSearchProps('title'),
         ellipsis: {
            showTitle: false,
         },
         render: payload => (
            <Tooltip placement="topLeft" title={payload.title}>
               <Text>
                  {
                     payload.title
                  }
               </Text>
            </Tooltip>
         ),
         width: '65%'
      },
      {
         title: 'Subject',
         key: 'subject_desc',
         ...getColumnSearchProps('subject_desc'),
         ellipsis: {
            showTitle: false,
         },
         render: payload => (
            <Tooltip placement="topLeft" title={payload.subject_desc}>
               <Text>
                  {
                     payload.subject_desc
                  }
               </Text>
            </Tooltip>
         ),
         width: '20%'
      },
      {
         title: <div style={{ textAlign: 'center' }}>Report</div>,
         render: payload => (
            <>
               <div style={{ textAlign: 'center' }}>
                  <Tooltip placement='top' title={"Show Summary Report"}>
                     <AntDIcons.SolutionOutlined style={{ cursor: 'pointer' }} onClick={() => handleShowReport(payload.id, payload.subject, payload.subject_desc, payload.standard_level_name)} />
                  </Tooltip>
               </div>
            </>

         ),
         width: '15%',
      },
   ].filter(item => !item.hidden);

   const getSections = (level_id) => {
      axios.get(`${process.env.REACT_APP_API_LOOKUP}/v2/sectionlistbylevel?level=${level_id}`).then((response) => {
         if (response.data) {
            const data = response.data;
            setSectionList(data);
         }
      }, (error) => {
      });
   }

   const getDiagnosticTitles = () => {
      setListLoading(true);

      var url = `${process.env.REACT_APP_API_BRAINEE_DIAGNOSTIC}/v3/getlist?t=${type}&l=${level}`;

      axios.get(url).then((response) => {
         if (response.data) {
            const data = response.data;

            getSections(level);
            setDiagnosticList(data);
            setListLoading(false);
         }
      }, (error) => {
         setListLoading(false);
      });
   }

   const displaySummaryReport = () => {
      if (!showSummaryReport) return null;

      const commonProps = {
         assessment_id: assessmentID,
         template,
         section_id: selectedSections,
         section_desc: selectedSectionsDesc,
         diagnostic_type: props.type,
         subject: subjectDesc,
         subject_id: subject,
         school_year: props.school_year,
      };

      const reportFamily = getReportFamily(template, subject);
      let ReportComponent = SHSSummaryReport;

      if (reportFamily === 'ems') {
         ReportComponent = EMSSummaryReport;
      } else if (reportFamily === 'mt') {
         ReportComponent = MTSummaryReport;
      }

      return ReportComponent ? (
         <ReportComponent {...commonProps} />
      ) : (
         Modal.info({ content: 'Report not yet available.', centered: true })
      );
   };

   const handleDownloadReport = (report_type = 'summary') => {
      var element = null;
      var reportFileName = "";

      if (report_type === 'summary') {
         element = document.getElementById('summary-report');
         reportFileName = 'Summary Results ' + (props.type === 'pre' ? 'Pre' : 'Post') + ' Test for ' + props.template + '.pdf';
      }

      if (element !== null) {
         var opt = {
            margin: 0.5,
            filename: reportFileName,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
         };

         // New Promise-based usage:
         html2pdf().from(element).set(opt).save();
      }
   }

   const handleChange = (val) => {
      setSectionOption(val);
   }

   const handleSectionSelect = (val) => {
      var selected_sections = val.length > 0 ? val.join(",") : "";
      setSelectedSections(selected_sections);

      var section_desc = "";

      val.forEach((element) => {
         let item = sectionList.find(o => o.value === element);

         if (section_desc !== "")
            section_desc += ", ";

         section_desc += item.name;
      });

      setSelectedSectionsDesc(section_desc);
   }

   const handleShowReport = (assessment_id, subject, subject_desc, level_desc) => {
      if (selectedSections !== "" && selectedSections !== "-1") {
         setAssessmentID(assessment_id);
         setSubject(subject);
         setSubjectDesc(subject_desc);
         setTemplate(level_desc);
         setShowSummaryReport(true);
      } else {
         message.info('Please select sections to include in the report.')
      }
   }

   return (
      <>
         <div className="dashboard-item-inner3">
            <Row gutter={[10, 10]} style={{ paddingBottom: "20px" }} >
               <Col span={24}>
                  <p>Sections to be included the report</p>
                  {/* <Segmented
                     key={'uid'}
                     // value={1}
                     defaultValue={sectionOption}
                     block
                     options={[
                        {
                           label: 'All',
                           value: 0,
                        },
                        {
                           label: 'Select',
                           value: 1,
                        },
                     ]}
                     onChange={(val) => handleChange(val)}
                  /> */}
                  {
                     <Select
                        showArrow
                        size='middle'
                        style={{ width: '100%' }}
                        allowClear
                        placeholder="Select Sections"
                        onChange={(val) => handleSectionSelect(val)}
                        mode="multiple"
                     >
                        {
                           sectionList !== null &&
                           sectionList.map(({ value, name }) => {
                              return <Option key={value} value={value}>{name}</Option>
                           })
                        }
                     </Select>
                  }

               </Col>
               {/* <Col xs={24} sm={24} md={8} lg={8} xl={8} xxl={8}>
                  &nbsp;
               </Col> */}
            </Row>
            <Row gutter={[10, 10]} style={{ paddingBottom: "20px" }} >
               <Col span={24}>
                  <Table
                     rowKey={'uid'}
                     loading={listLoading}
                     size='small'
                     columns={columns}
                     dataSource={diagnosticList}
                     pagination={false}
                     scroll={{
                        x: '500px',
                        y: 'calc(100vh - 18.5rem)',
                     }}
                     footer={() =>
                        <Row style={{ width: "100%" }}>
                           <Col style={{ width: "70%" }}>{'Total Records: ' + diagnosticList.length}</Col>
                           <Col style={{ width: "30%", textAlign: "right" }}>&nbsp;</Col>
                        </Row>
                     }
                  />
               </Col>
            </Row>

         </div >

         <Modal
            key='show-diagnostic-summary-report'
            title={<div><Image src='../images/brainee_mascot.png' preview={false} height="30px" style={{ minWidth: '30px', maxHeight: '100px' }} /> {"Summary Results  " + (props.type === 'pre' ? 'Pre' : 'Post') + " Test for " + template}</div>}
            okText={"Close"}
            cancelButtonProps={{ style: { display: 'none' } }}
            okButtonProps={{ shape: "round", style: { display: 'none' } }}
            centered
            destroyOnClose={true}
            open={showSummaryReport}
            width='75%'
            style={{ maxWidth: '900px' }}
            footer={[
               <Space>
                  <Button
                     shape='round' type="primary"
                     loading={downloadStatus}
                     onClick={() => handleDownloadReport('summary')}
                  >
                     Download
                  </Button>
                  <Button shape='round' type='primary' onClick={() => { setShowSummaryReport(false); }}>
                     Close
                  </Button>
               </Space>
            ]}
         >
            <div className='essay_list_container' style={{ background: "#fff" }}>
               <div className='section_to_print'>
                  {displaySummaryReport()}
               </div>
            </div>
         </Modal>
      </>
   );
}
